import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, ChevronDown, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import LanguageSwitcher from '@/components/common/LanguageSwitcher.jsx';

export default function Header() {
  const { t } = useTranslation();
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [openDropdown,  setOpenDropdown]  = useState(null); // 'subsystems' | 'audience' | null

  const subsystemLinks = [
    { to: '/subsystems/rerr',  label: t('nav.subsystemLinks.rerr.label'),  tagline: t('nav.subsystemLinks.rerr.tagline') },
    { to: '/subsystems/ifps',  label: t('nav.subsystemLinks.ifps.label'),  tagline: t('nav.subsystemLinks.ifps.tagline') },
    { to: '/subsystems/firl',  label: t('nav.subsystemLinks.firl.label'),  tagline: t('nav.subsystemLinks.firl.tagline') },
    { to: '/subsystems/veris', label: t('nav.subsystemLinks.veris.label'), tagline: t('nav.subsystemLinks.veris.tagline') },
  ];

  const audienceLinks = [
    { to: '/for-citizens', label: t('nav.audienceLinks.citizens.label'), tagline: t('nav.audienceLinks.citizens.tagline') },
    { to: '/for-agencies', label: t('nav.audienceLinks.agencies.label'), tagline: t('nav.audienceLinks.agencies.tagline') },
    { to: '/for-partners', label: t('nav.audienceLinks.partners.label'), tagline: t('nav.audienceLinks.partners.tagline') },
  ];

  const closeAll = () => {
    setMobileOpen(false);
    setOpenDropdown(null);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur">
      <div className="container-wide flex h-16 items-center justify-between">
        <Link to="/" onClick={closeAll} className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white">
            <Shield className="h-5 w-5" strokeWidth={2.4} />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-wide text-slate-900">{t('brand.name')}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              {t('brand.full')}
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          <Dropdown
            label={t('nav.subsystems')}
            open={openDropdown === 'subsystems'}
            onToggle={() => setOpenDropdown(o => o === 'subsystems' ? null : 'subsystems')}
            onClose={() => setOpenDropdown(null)}
            items={subsystemLinks}
            footerLink={{ to: '/subsystems', label: `${t('common.viewAll')} →` }}
          />
          <Dropdown
            label={t('nav.whoWeServe')}
            open={openDropdown === 'audience'}
            onToggle={() => setOpenDropdown(o => o === 'audience' ? null : 'audience')}
            onClose={() => setOpenDropdown(null)}
            items={audienceLinks}
          />
          <NavItem to="/security">{t('nav.security')}</NavItem>
          <NavItem to="/about">{t('nav.about')}</NavItem>
          <NavItem to="/contact">{t('nav.contact')}</NavItem>
        </nav>

        <div className="hidden items-center gap-1 lg:flex">
          <LanguageSwitcher variant="light" />
          <Link to="/login"    className="btn-ghost">{t('common.signIn')}</Link>
          <Link to="/register" className="btn-primary">{t('common.signUp')}</Link>
        </div>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 lg:hidden"
          aria-label={mobileOpen ? t('common.closeMenu') : t('common.openMenu')}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(o => !o)}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="border-t border-slate-200 bg-white lg:hidden">
          <div className="container-wide flex flex-col gap-1 py-4">
            <MobileGroup label={t('nav.subsystems')}>
              {subsystemLinks.map(l => (
                <MobileLink key={l.to} to={l.to} onClick={closeAll}>
                  <span className="font-semibold">{l.label}</span>
                  <span className="block text-xs text-slate-500">{l.tagline}</span>
                </MobileLink>
              ))}
              <MobileLink to="/subsystems" onClick={closeAll}>{t('common.viewAll')} →</MobileLink>
            </MobileGroup>

            <MobileGroup label={t('nav.whoWeServe')}>
              {audienceLinks.map(l => (
                <MobileLink key={l.to} to={l.to} onClick={closeAll}>
                  <span className="font-semibold">{l.label}</span>
                  <span className="block text-xs text-slate-500">{l.tagline}</span>
                </MobileLink>
              ))}
            </MobileGroup>

            <MobileLink to="/security" onClick={closeAll}>{t('nav.security')}</MobileLink>
            <MobileLink to="/about"    onClick={closeAll}>{t('nav.about')}</MobileLink>
            <MobileLink to="/contact"  onClick={closeAll}>{t('nav.contact')}</MobileLink>

            <div className="mt-3 flex items-center gap-2 border-t border-slate-200 pt-4">
              <LanguageSwitcher variant="light" />
              <Link to="/login"    onClick={closeAll} className="btn-secondary flex-1">{t('common.signIn')}</Link>
              <Link to="/register" onClick={closeAll} className="btn-primary   flex-1">{t('common.signUp')}</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function NavItem({ to, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          'rounded-md px-3 py-2 text-sm font-medium transition',
          isActive ? 'text-sky-700' : 'text-slate-700 hover:bg-slate-100',
        )
      }
    >
      {children}
    </NavLink>
  );
}

function Dropdown({ label, open, onToggle, onClose, items, footerLink }) {
  const ref = useRef(null);

  // Close on outside click via document mousedown — replaces the prior
  // `<div fixed inset-0 z-10 onClick={onClose}>` overlay which captured
  // clicks across the whole viewport (so clicking Sign in / Request
  // access while a dropdown was open never reached the Link beneath).
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e) {
      if (!ref.current?.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, onClose]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={clsx(
          'inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition',
          open ? 'bg-slate-100 text-slate-900' : 'text-slate-700 hover:bg-slate-100',
        )}
        aria-expanded={open}
      >
        {label} <ChevronDown className={clsx('h-4 w-4 transition', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-2 w-80 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          {items.map(item => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onClose}
              className="block rounded-md px-3 py-2.5 transition hover:bg-slate-50"
            >
              <div className="text-sm font-semibold text-slate-900">{item.label}</div>
              <div className="text-xs text-slate-500">{item.tagline}</div>
            </Link>
          ))}
          {footerLink && (
            <Link
              to={footerLink.to}
              onClick={onClose}
              className="mt-1 block border-t border-slate-100 px-3 pt-3 pb-1 text-xs font-medium text-sky-600 hover:text-sky-700"
            >
              {footerLink.label}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function MobileGroup({ label, children }) {
  return (
    <div className="py-1">
      <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      {children}
    </div>
  );
}

function MobileLink({ to, onClick, children }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block rounded-md px-3 py-2 text-sm text-slate-800 transition hover:bg-slate-100"
    >
      {children}
    </Link>
  );
}
